/* eslint-disable react/prop-types */
import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  Card,
  IconButton,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
} from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { Delete, DragIndicator, ClearAll } from '@mui/icons-material';
import { dispatch } from '../../../../redux/store';
import { getWatchlistSuccess } from '../../../../redux/slices/trades';
import useAuth from '../../../../hooks/useAuth';
import modifyScriptName from '../../../../utils/modifyScriptName';

// Using "shouldForwardProp" to prevent the isDragging prop from being passed to the DOM element
const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})(({ theme, isDragging }) => ({
  height: 52,
  backgroundColor: isDragging
    ? alpha(theme.palette.primary.main, 0.1)
    : 'inherit',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '& > td': {
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    '&:first-of-type': {
      paddingLeft: theme.spacing(2),
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(2),
    },
  },
  '&:nth-of-type(odd)': {
    backgroundColor: isDragging
      ? alpha(theme.palette.primary.main, 0.1)
      : alpha(theme.palette.background.neutral, 0.02),
  },
  transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
  position: 'relative',
  ...(isDragging && {
    background: theme.palette.background.paper,
    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.24)}`,
    borderRadius: 1,
    cursor: 'grabbing',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    '& td': {
      borderBottom: 'none',
    },
  }),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: theme.spacing(1.5),
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    '&:first-of-type': {
      paddingLeft: theme.spacing(2),
      borderTopLeftRadius: theme.shape.borderRadius,
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(2),
      borderTopRightRadius: theme.shape.borderRadius,
    },
  },
}));

const WatchListItem = ({ showDelete, filteredItems, selectedTab }) => {
  const watchlist = useSelector((state) => state?.trade?.watchlist);
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // Create a new array with the updated order
    const newItems = Array.from(filteredItems);
    const [removed] = newItems.splice(sourceIndex, 1);
    newItems.splice(destinationIndex, 0, removed);

    // Update the watchlist with the new order
    const updatedWatchlist = {
      ...watchlist,
      [selectedTab]: newItems
    };

    // Save to localStorage and update state
    localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
    dispatch(getWatchlistSuccess(updatedWatchlist));
  };
  const handleDeleteItem = (itemId) => {
    const updatedWatchlist = { ...watchlist };
    updatedWatchlist[selectedTab] = updatedWatchlist[selectedTab].filter((item) => item._id !== itemId);
    localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
    dispatch(getWatchlistSuccess(updatedWatchlist));
  };

  const handleClearAll = () => {
    const updatedWatchlist = { ...watchlist };
    updatedWatchlist[selectedTab] = [];
    localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
    dispatch(getWatchlistSuccess(updatedWatchlist));
  };

  if (filteredItems.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No Scripts Added
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add scripts to your watchlist to track their prices
        </Typography>
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Card
        sx={{
          borderRadius: 2,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
          boxShadow: (theme) => theme.shadows[0]
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {showDelete && (
                  <>
                    <StyledTableCell width={64} align="center">
                      <IconButton
                        onClick={handleClearAll}
                        sx={{
                          p: 0.5,
                          color: 'error.main',
                          '&:hover': {
                            bgcolor: 'error.lighter',
                            transform: 'scale(1.1)'
                          },
                          transition: 'transform 0.2s'
                        }}
                        title="Remove All Scripts"
                      >
                        <ClearAll fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                    <StyledTableCell width={64} align="center">Delete</StyledTableCell>
                  </>
                )}

                {/* <StyledTableCell width={64} /> */}
                <StyledTableCell align="center">Symbol</StyledTableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId="droppable" type="WATCHLIST_ITEM">
              {(provided, snapshot) => (
                <TableBody
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{
                    backgroundColor: snapshot.isDraggingOver
                      ? (theme) => alpha(theme.palette.primary.lighter, 0.2)
                      : 'inherit',
                    transition: (theme) => theme.transitions.create('background-color'),
                  }}
                >
                  {filteredItems.map((item, index) => (
                    <Draggable key={item._id} draggableId={item._id} index={index}>
                      {(provided, snapshot) => (
                        <StyledTableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          isDragging={snapshot.isDragging}
                          style={{
                            ...provided.draggableProps.style,
                            transform: snapshot.isDragging && provided.draggableProps.style?.transform
                              ? provided.draggableProps.style.transform
                              : 'none',
                            zIndex: snapshot.isDragging ? 999 : 1,
                          }}
                        >
                          {showDelete && (
                            <TableCell align="center">
                              <IconButton
                                onClick={() => handleDeleteItem(item._id)}
                                sx={{
                                  p: 0.5,
                                  color: 'error.main',
                                  '&:hover': {
                                    bgcolor: 'error.lighter',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'transform 0.2s'
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: '4px',
                                transition: 'all 0.2s',
                                ...(snapshot.isDragging && {
                                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                }),
                              }}
                            >
                              <DragIndicator
                                sx={{
                                  color: (theme) => snapshot.isDragging
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                  fontSize: 20,
                                  cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                  '&:hover': {
                                    color: 'primary.main'
                                  }
                                }}
                              />
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="subtitle2"
                              align="center"
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 0.2,
                              }}
                            // noWrap
                            >
                              {modifyScriptName(item.tradingsymbol, item.expiry)}
                            </Typography>
                          </TableCell>
                        </StyledTableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </TableContainer>
      </Card>
    </DragDropContext>
  );
};

export default WatchListItem;
